import {
	animate,
	group,
	query,
	stagger,
	style,
	transition,
	trigger
} from '@angular/animations';

/**
 * One curve and one duration for a list opening AND a list closing, on
 * purpose.
 *
 * The first version used a fast-start curve to open and a slower standard one
 * to close, on the theory that arrivals should feel quick. Measured frame by
 * frame when switching Partner Ads → Social Listening: the opening list grew
 * 56px and then 103px in its first two frames while the closing one had given
 * up 3px, so for a moment BOTH stood nearly full height and every row below
 * overshot its final place by 32px before settling back. With the same curve
 * on both, the column's total height is (closing × (1 − p)) + (opening × p) —
 * a straight line between the two states, so nothing below them can overshoot.
 *
 * The children's own fade has no layout in it and keeps the livelier curve.
 */
const SUBMENU_MOTION = '260ms cubic-bezier(0.4, 0, 0.2, 1)';
const SUBMENU_ITEM_MOTION = '220ms cubic-bezier(0.22, 1, 0.36, 1)';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AsyncPipe, NgClass, NgFor, NgIf, NgStyle } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ComponentRef,
	ElementRef,
	HostListener,
	NgZone,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatList, MatListItem } from '@angular/material/list';
import {
	MatSidenav,
	MatSidenavContainer,
	MatSidenavContent
} from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import {
	ActivatedRoute,
	Event as RouterEvent,
	NavigationCancel,
	NavigationEnd,
	NavigationError,
	NavigationStart,
	Router,
	RouterOutlet
} from '@angular/router';
import { SidenavService } from '@brand-lolly/app/_services/side-nav.service';
import { BookingService } from '@brand-lolly/app/services/booking.service';
import { ClickAnalyticsService } from '@brand-lolly/app/services/click-analytics.service';
import { LandingPageService } from '@brand-lolly/app/services/landing-page.service';
import { UserStatesHttpService } from '@brand-lolly/app/services/user-states.http.service';
import {
	UserStatesModel,
	UserStatesService
} from '@brand-lolly/app/services/user-states.service';
import {
	ChildrenMenuDialog,
	ChildrenMenuItem
} from '@brand-lolly/app/sidenav/sidenav/children-menu.dialog';
import {
	SlEntitiesState,
	SocialListeningNavService
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/social-listening-nav.service';
import { CreditBalanceIndicatorComponent } from '@brand-lolly/components/credit-balance-indicator/credit-balance-indicator.component';
import { CreditLowBalanceBannerComponent } from '@brand-lolly/components/credit-low-balance-banner/credit-low-balance-banner.component';
import { AnaPageComposerComponent } from '@brand-lolly/components/ana-page-composer/ana-page-composer.component';
import {
	isMacPlatform,
	isUniversalSearchShortcut,
	UniversalSearchService
} from '@brand-lolly/components/universal-search/universal-search.service';
import { NgSelectConfig } from '@ng-select/ng-select';
import {
	BurgerButtonComponent,
	IconComponent,
	LollySpinnerLoaderComponent,
	OnOffToggleComponent,
	SkeletonLoaderComponent,
	TypographyComponent,
	UiTextComponent
} from 'components';
import { BehaviorSubject, fromEvent, of, Subject, Subscription } from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	filter,
	finalize,
	skip,
	take,
	takeUntil
} from 'rxjs/operators';
import { AuthenticationV2Service } from 'src/app/authentication-v2/services/authentication-v2.service';
import { SubscriptionsStateService } from 'src/app/billing/services/subscriptions-state.service';
import { TeamPermissionsStateService } from 'src/app/user-management/team/services/team-permissions-state.service';
import { TeamAction } from 'src/app/user-management/team/types/team.types';
import {
	requireGatedNavigationAccess,
	requireNavigationAccess
} from 'src/app/shared/navigation/navigation-product-access';
import { GetStartedDataModel } from 'src/app/shared/models/first-timer-user.model';
import { SharedService } from 'src/app/shared/services/shared.service';
import { UtilsService } from 'src/app/shared/utils';
import { buildGeneratedValues } from '../../../../build-generated-values';
import { CommentAutopilotAccessStateService } from '@brand-lolly/app/social-media-influencer/modules/comment-autopilot/services/comment-autopilot-access-state.service';
import { isCommentAutopilotEnabled } from '@brand-lolly/app/social-media-influencer/modules/comment-autopilot/guards/comment-autopilot-enabled.guard';
import { isNavigationEntryShown } from '@brand-lolly/app/shared/navigation/navigation-entry-visibility';
import { environment } from '../../../environments/environment';
import { StorageKey } from '../../_models/local-storage-key';
import { AnaAssistantFacadeService } from '../../_services/ana-assistant-facade.service';
import { AuthenticationService } from '../../_services/authentication.service';
import { ScrollService } from '../../_services/scroll.service';
import { AvatarComponent } from '../../shared-components/avatar.component';
import { HintsComponent } from '../../shared-components/hints/hints.component';
import { AnaAssistantPanelComponent } from '../../shared/ana-assistant-panel/ana-assistant-panel.component';
import {
	IMainMenuGroup,
	IProductMenuGroup
} from '../_models/nav-group.interface';
import { KnowledgeBaseComponent } from '../component-pieces/knowledge-base/components/knowledge-base/knowledge-base.component';
import { BurgerMenuComponent } from '../components/burger-menu/burger-menu.component';
import { ProfileMenuComponent } from './profile-menu.component';

/** Where the rail's collapsed state is remembered, per browser. */
const RAIL_COLLAPSED_KEY = 'lolly.shell.railCollapsed';
/** How the rail tells the rest of the page how wide it is. */
const RAIL_COLLAPSED_BODY_CLASS = 'shell-rail-collapsed';

// TRAP: OPAQUE only — a translucent tint copied onto the row behind the page is
// composited a second time by the element that carries it, and the page darkens.
function isOpaqueColor(color: string): boolean {
	if (/^rgb\(/.test(color)) {
		return true;
	}
	const alpha = /^rgba\([^,]+,[^,]+,[^,]+,\s*([\d.]+)\s*\)$/.exec(color);
	return alpha !== null && parseFloat(alpha[1]) >= 1;
}

/**
 * The Social Listening entry's product and its explaining page, read from the
 * one table the route guards read. The click gate below and
 * `socialListeningAccessGuard` can therefore not disagree about whether the
 * entry is open — an item locked in the menu and ungated on its route is the
 * defect this table exists to make impossible.
 */
const SOCIAL_LISTENING_ACCESS =
	requireGatedNavigationAccess('social-listening');

@Component({
	standalone: true,
	selector: 'app-sidenav-group',
	templateUrl: './sidenav-group.component.html',
	styleUrls: ['./sidenav-group.component.scss'],
	providers: [ScrollService],
	animations: [
		/**
		 * A section's children opening and closing in the list.
		 *
		 * Without it, switching from one open section to another removed one
		 * block and inserted the other in the same frame, and every row between
		 * them jumped to its new place. Now the closing list folds while the
		 * opening one unfolds, both on height, so the rows between them travel
		 * instead of teleporting.
		 *
		 * Height to '*' is measured by Angular at run time — the one thing CSS
		 * cannot do across browsers yet (interpolate-size is Chromium-only).
		 * Padding goes with it, or a "closed" list keeps 6px of air.
		 *
		 * The children themselves fade in on a 25ms stagger, a few pixels from
		 * above — enough to read as a list arriving rather than a block
		 * appearing, and short enough to be over before the reader's eye lands.
		 */
		trigger('submenuExpand', [
			transition(':enter', [
				style({
					height: 0,
					paddingTop: 0,
					paddingBottom: 0,
					opacity: 0,
					overflow: 'hidden'
				}),
				group([
					animate(
						SUBMENU_MOTION,
						style({
							height: '*',
							paddingTop: '*',
							paddingBottom: '*',
							opacity: 1
						})
					),
					query(
						'.submenu-item',
						[
							style({
								opacity: 0,
								transform: 'translateY(-4px)'
							}),
							stagger(25, [
								animate(
									SUBMENU_ITEM_MOTION,
									style({ opacity: 1, transform: 'none' })
								)
							])
						],
						{ optional: true }
					)
				])
			]),
			transition(':leave', [
				style({ height: '*', overflow: 'hidden' }),
				animate(
					SUBMENU_MOTION,
					style({
						height: 0,
						paddingTop: 0,
						paddingBottom: 0,
						opacity: 0
					})
				)
			])
		]),
		trigger('slideInDown', [
			transition(':enter', [
				style({
					opacity: 0,
					transform: 'translateY(10px)'
				}),
				animate(
					'150ms ease-out',
					style({
						opacity: 1,
						transform: 'translateY(0)'
					})
				)
			]),
			transition(':leave', [
				animate(
					'150ms ease-in',
					style({
						opacity: 0,
						transform: 'translateY(10px)'
					})
				)
			])
		])
	],
	imports: [
		NgIf,
		NgClass,
		MatSidenav,
		NgFor,
		MatList,
		MatListItem,
		NgStyle,
		MatToolbar,
		SkeletonLoaderComponent,
		UiTextComponent,
		OnOffToggleComponent,
		MatTooltip,
		IconComponent,
		AvatarComponent,
		ProfileMenuComponent,
		BurgerButtonComponent,
		MatSidenavContainer,
		MatSidenavContent,
		RouterOutlet,
		LollySpinnerLoaderComponent,
		BurgerMenuComponent,
		HintsComponent,
		AnaAssistantPanelComponent,
		AsyncPipe,
		CreditBalanceIndicatorComponent,
		CreditLowBalanceBannerComponent,
		AnaPageComposerComponent,
		TypographyComponent
	]
})
export class SidenavGroupComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('sidenav') public sidenav: MatSidenav;

	@ViewChild('settingsIcon') settingsIcon: ElementRef;

	@ViewChild('railSearch') private railSearch?: ElementRef<HTMLElement>;

	/**
	 * The page's scroller, and the element whose scroll a covering panel takes
	 * over. It is `mat-sidenav-content`, NOT `.main-container`: Material gives
	 * `.mat-drawer-content` `height: 100%; overflow: auto` inside a container
	 * it sets to `overflow: hidden`, and `.main-container` is pinned to that
	 * same height, so it never overflows and never scrolls. Putting a lock on
	 * it does nothing and reads `scrollTop === 0` forever.
	 */
	@ViewChild('shellScroller', { read: ElementRef })
	shellScroller: ElementRef<HTMLElement>;

	/** The column the routed page renders into; its descendants are searched for the page's surface. */
	@ViewChild('shellContentMain', { read: ElementRef })
	shellContentMain: ElementRef<HTMLElement>;

	/** Matches the width transition on .shell-ana-panel. */
	private static readonly PANEL_WIDTH_TRANSITION_MS = 320;

	// TRAP: longer than the transition on purpose -- the transition starts on
	// the frame AFTER the change, so a timer of exactly its length can put the
	// panel back in flow one frame early, at a width still short of the end.
	private static readonly PANEL_DETACH_RELEASE_MS =
		SidenavGroupComponent.PANEL_WIDTH_TRANSITION_MS + 50;

	// LIMIT: a surface painted later than the last re-read, or changed by an in-page
	// view switch, is not seen until the next navigation — T-2377.
	private static readonly PAGE_SURFACE_RECHECK_MS = [300, 1000];

	private static readonly PAGE_SURFACE_MAX_DEPTH = 6;

	/**
	 * The page background the shell paints behind the content, the reserved
	 * room under the composer and the Ask Ana panel, and hands to the composer's
	 * band: the route's declared `shellBackground` when it has one, otherwise
	 * the colour of the page's own bottom surface. Null — a white page, and the
	 * gap between two routes — falls back to the shell's default in the
	 * stylesheet.
	 */
	public shellBackground: string | null = null;

	private declaredShellBackground: string | null = null;
	private detectedPageBackground: string | null = null;

	private pageSurfaceFrame: number | null = null;
	private pageSurfaceTimers: ReturnType<typeof setTimeout>[] = [];

	/**
	 * True while the panel is out of the content flow: covering the page, or
	 * still animating back from having covered it.
	 */
	public panelDetached = false;

	/** True while the page behind a covering panel must not scroll. */
	public shellScrollLocked = false;

	private lockedScrollTop = 0;
	private panelDetachTimer: ReturnType<typeof setTimeout> | null = null;
	private scrollerGutterObserver?: ResizeObserver;

	public mainMenuGroup = new Map<number, IMainMenuGroup>();

	public productMenuGroup: Map<number, IProductMenuGroup>;

	public showSidenav = true;
	public showSpinner = false;
	public timestamp: string;
	public dropdownIsVisible: boolean = false;

	/**
	 * Whether the rail is showing marks only.
	 *
	 * The rail is 240px so a destination can be READ, which is the whole point
	 * of widening it — but 240px is 150px the page does not have on a wide
	 * table, so the old density stays reachable. Remembered per browser: a
	 * reader who collapses it wants it collapsed tomorrow too, and the state is
	 * a display preference, not something the account should carry.
	 */
	public railCollapsed = false;

	/**
	 * Angular's animations do not consult prefers-reduced-motion — the CSS
	 * transitions in this rail do, these would not. Bound to [@.disabled] on
	 * the list, which makes every trigger under it land in its end state at
	 * once.
	 */
	public readonly reduceMotion =
		typeof window !== 'undefined' &&
		!!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

	/**
	 * Which row has its submenu open, and what is in it.
	 *
	 * The children used to be a CDK overlay pinned to the row's right edge — a
	 * flyout, which is what a 90px rail of marks had to do because there was
	 * nowhere to put them. A rail wide enough to read is wide enough to hold
	 * them, and a list that grows in place keeps the parent visible above its
	 * children instead of covering the page beside it.
	 *
	 * One at a time: opening a second closes the first. The rail is a list of
	 * destinations, not a tree to browse.
	 */
	public openSubmenuIndex: number | null = null;
	public openSubmenuItems: ChildrenMenuItem[] = [];

	/** Whether this row's children are the ones on screen. */
	public isSubmenuOpen(index: number): boolean {
		return this.openSubmenuIndex === index;
	}

	/**
	 * Whether a row opens children at all — ONE definition, read by the click
	 * handler and by the chevron alike.
	 *
	 * They used to disagree. The click asked `children?.length` and the chevron
	 * asked `hasChildren`, which is declared `false` on every item in the menu,
	 * including any that has children. Such a row opened a submenu and
	 * advertised nothing: it looked like a plain link that behaved oddly, and it
	 * was reported as a missing feature.
	 */
	public hasSubmenu(item: IProductMenuGroup): boolean {
		return !!item.children?.length || !!item.slDynamic;
	}

	private closeSubmenu(): void {
		this.openSubmenuIndex = null;
		this.openSubmenuItems = [];
		this.activeSubmenuIndex = null;
	}

	/**
	 * Open a row's children in place, or shut them if they are already open.
	 *
	 * Collapsed, there is no room for a nested list, so the rail expands first
	 * and the children open into it — rather than maintaining a second
	 * presentation for the same menu.
	 */
	private openSubmenu(index: number, items: ChildrenMenuItem[]): void {
		if (this.openSubmenuIndex === index) {
			this.closeSubmenu();
			return;
		}
		if (this.railCollapsed) {
			this.toggleRail();
		}
		this.openSubmenuIndex = index;
		this.openSubmenuItems = items;
		this.recomputeActiveSubmenuItem();
	}

	/** A child's own route, which is the parent's plus its own. */
	private childItems(item: IProductMenuGroup): ChildrenMenuItem[] {
		return (item.children ?? []).map(child => ({
			title: child.title,
			icon: child.iconAlias,
			path: `${item.route}${child.route}`
		}));
	}

	/**
	 * Which child, if any, is the page on screen — by index into
	 * `openSubmenuItems`, so the template asks once per row and never recomputes
	 * a match per item per change-detection pass.
	 *
	 * Recomputed on navigation and whenever the open list changes, which is the
	 * only pair of things that can move it.
	 */
	public activeSubmenuIndex: number | null = null;

	/**
	 * Match the current URL against what each open item says it owns.
	 *
	 * Deliberately no guessing. The first version accepted a final segment that
	 * merely STARTED with the item's own, on the theory that it covered the
	 * redirect guards' `-setup` pages. It covered exactly the ones whose names
	 * happened to start the same way — `competitors-setup`, `industry-setup` —
	 * and missed `brand-setup`, where Brands lands, and every Reports page but
	 * the one the item opens on. Each item now lists its routes (`matches`), and
	 * a route either is one of them or it is not.
	 *
	 * Path-SEGMENT prefixes, so `/reports` owns `/reports/create` but
	 * `/competitor` would never own `/competitors-setup`. Longest wins, so a
	 * more specific declaration beats a broader one if two ever overlap. A page
	 * nobody declares — the shared analysis-loading step between two setups —
	 * marks nothing, which is the honest answer for a page that belongs to
	 * neither.
	 */
	private recomputeActiveSubmenuItem(): void {
		const current = this.urlSegments(this.router.url);
		let best: number | null = null;
		let bestDepth = 0;

		this.openSubmenuItems.forEach((item, index) => {
			for (const route of item.matches ?? [item.path]) {
				const owned = this.urlSegments(route);
				if (
					owned.length &&
					owned.length <= current.length &&
					owned.every((seg, i) => seg === current[i]) &&
					owned.length > bestDepth
				) {
					best = index;
					bestDepth = owned.length;
				}
			}
		});

		this.activeSubmenuIndex = best;
	}

	/** Path segments only — a query string is not part of where you are. */
	private urlSegments(url: string): string[] {
		return url.split(/[?#]/)[0].split('/').filter(Boolean);
	}

	/**
	 * Go to a child, and leave the list OPEN.
	 *
	 * Closing it was the flyout's behaviour — a panel over the page has to get
	 * out of the way once a choice is made. An accordion is the section's own
	 * table of contents: the next thing a reader does after opening Competitors
	 * is often open Industry, and shutting the list made them reopen it for
	 * every hop. The marker moves on NavigationEnd.
	 */
	public onSubmenuItemClick(child: ChildrenMenuItem): void {
		if (child.disabled) {
			return;
		}
		this.router.navigateByUrl(child.path);
	}

	public toggleRail(): void {
		this.railCollapsed = !this.railCollapsed;
		this.publishRailState();
		try {
			localStorage.setItem(
				RAIL_COLLAPSED_KEY,
				this.railCollapsed ? '1' : '0'
			);
		} catch {
			// Private windows and blocked site data throw on write. The rail
			// still toggles; it just will not remember.
		}
	}

	private restoreRailState(): void {
		try {
			this.railCollapsed =
				localStorage.getItem(RAIL_COLLAPSED_KEY) === '1';
		} catch {
			this.railCollapsed = false;
		}
		this.publishRailState();
	}

	/**
	 * Say on the BODY how wide the rail is.
	 *
	 * The Book a Call widget is fixed to the bottom-left corner and lives in
	 * app.component, outside this component's tree — so it cannot read the
	 * rail's state through an input or a CSS variable on the content column.
	 * A body class is the one hook both can see. Removed on destroy, because
	 * the shell is torn down on sign-out and a stale class would shrink the
	 * widget on a page that has no rail at all.
	 */
	private publishRailState(): void {
		document.body.classList.toggle(
			RAIL_COLLAPSED_BODY_CLASS,
			this.railCollapsed
		);
		// The same fact for anything that needs it in TypeScript rather than in
		// CSS — the Book a Call widget's tooltip is the first such reader.
		this.sidenavService.setRailCollapsed(this.railCollapsed);
	}
	public currentRoute: string;
	public isBalanceLoading: boolean = false;
	expandMenu: boolean;
	filteredMenuList: IProductMenuGroup[] = [];
	profileOpen: any;
	activeRoute: string;
	brandName: string;
	profileImage: string;
	allDataLoading = false;
	userFName: string;
	userLName: string;
	userEmail: string;
	urlOrigin: string;
	activeParentIndex: number;
	pageLoadingRoute: boolean;

	public primaryColor: string = '#005ed8';

	public hideSideBar: number = 0;
	private expandAccountMenuSubscription = new Subscription();
	private notificationSubscription: Subscription;
	private unsubscriber$ = new Subject<void>();
	public hasSMI: boolean;
	/**
	 * Whether this person may change what the account pays — the profile and
	 * burger menus hide their Pricing entry when they may not.
	 *
	 * True until the role matrix answers: the same fail-open the billing guard
	 * uses, and the route behind the entry refuses a member either way.
	 */
	public canManageBilling: boolean = true;
	public getStartedLoading: boolean = false;
	public getStartedData: GetStartedDataModel;
	public resetTimerInSeconds: number;
	private resetTimerInterval: number;

	userStates: BehaviorSubject<UserStatesModel>;

	burgerMenuOpen = false;

	private overlayRef?: OverlayRef;
	private childrenMenuUnSubscriber = new Subject<void>();

	// ── Social Listening hover submenu state machine ──────────────────
	private readonly SL_HOVER_OPEN_DELAY = 150;
	private readonly SL_HOVER_CLOSE_DELAY = 250;
	private slOpenTimer: ReturnType<typeof setTimeout> | null = null;
	private slCloseTimer: ReturnType<typeof setTimeout> | null = null;
	private slMenuComponentRef?: ComponentRef<ChildrenMenuDialog>;

	private routerListenersInitialized = false;

	@HostListener('document:click', ['$event'])
	clickout(event) {
		if (!this.settingsIcon?.nativeElement.contains(event.target)) {
			this.profileOpen = false;
		}
	}

	// Ask Ana panel pushes content while it fits; once the viewport shrinks
	// (or the panel is dragged wide) the remaining content column could fall
	// below its usable minimum, so the panel must flip to an overlay. That
	// decision depends on the live viewport width — keep it fresh on resize.
	@HostListener('window:resize')
	onWindowResize(): void {
		this.anaFacade.notifyViewportResize();
	}

	readonly searchShortcutLabel = isMacPlatform() ? '⌘K' : 'Ctrl K';
	readonly searchShortcut =
		this.searchShortcutLabel === '⌘K' ? 'Meta+K' : 'Control+K';

	// TRAP: the bubble phase, and a prevented event is left alone — an editor
	// that binds the shortcut itself (the SL report editor's link) claims it by
	// preventing default before it reaches the document.
	@HostListener('document:keydown', ['$event'])
	onSearchShortcut(event: KeyboardEvent): void {
		if (event.defaultPrevented || !isUniversalSearchShortcut(event)) return;
		event.preventDefault();
		this.universalSearch.open(this.railSearch?.nativeElement ?? null);
	}

	openSearch(): void {
		this.universalSearch.open(this.railSearch?.nativeElement ?? null);
	}

	constructor(
		public router: Router,
		private authenticationService: AuthenticationService,
		private landingPageService: LandingPageService,
		private config: NgSelectConfig,
		private dialog: MatDialog,
		private scrollService: ScrollService,
		public cdRef: ChangeDetectorRef,
		private sharedService: SharedService,
		private authV2Service: AuthenticationV2Service,
		private sidenavService: SidenavService,
		public userStatesService: UserStatesService,
		private bookingService: BookingService,
		private userStatesHttpService: UserStatesHttpService,
		private clickAnalyticsService: ClickAnalyticsService,
		private overlay: Overlay,
		public anaFacade: AnaAssistantFacadeService,
		private ngZone: NgZone,
		private subscriptionsState: SubscriptionsStateService,
		private slNav: SocialListeningNavService,
		private teamPermissionsState: TeamPermissionsStateService,
		private universalSearch: UniversalSearchService,
		private commentAutopilotAccess: CommentAutopilotAccessStateService
	) {
		this.userStatesHttpService.init();

		this.userStates = this.userStatesService.userStates;

		this.pageLoadingRoute = true;
		this.mainMenuGroup = new Map();
		const megaMenus = this.authenticationService.allowedMegaRoutes$.value;
		try {
			megaMenus.forEach((item, index) => {
				this.mainMenuGroup.set(index + 1, item);
			});
		} catch (e) {}

		this.productMenuGroup = new Map([
			[
				0,
				{
					parentName: 'sansa',
					name: 'home',
					title: 'Home',
					icon: 'assets/icons/navbar/home.svg',
					iconAlias: 'nav_home',
					isSelected: false,
					hasChildren: false,
					hoverOver: false,
					childOpen: false,
					route: '/',
					childIndex: 0
				}
			],
			[
				1,
				{
					parentName: 'sansa',
					name: 'campaigns',
					title: 'Campaigns',
					icon: 'assets/icons/navbar/ai-projects.svg',
					iconAlias: 'nav_ai_projects',
					isSelected: false,
					hasChildren: false,
					hoverOver: false,
					childOpen: false,
					route: '/campaigns',
					childIndex: 0,
					click: () => {
						// The event id is deliberately NOT renamed with the
						// surface. Renaming it shows a cliff in every existing
						// funnel on release day and silently splits the series
						// in two; the label is what changed, not the thing being
						// counted. Revisit only alongside the Clarity funnel
						// step names, as one decision.
						this.clickAnalyticsService.sendClickEvent(
							'click_projects_page'
						);
					}
				}
			],
			// KEY 6, sitting between 1 and 2 on purpose: a Map iterates in INSERTION
			// order, so where a row appears is where it is written here and the
			// number is only a label. 6 was the free one; renumbering the rest to
			// make the labels read in order would touch every entry below for no
			// change in what anybody sees.
			[
				6,
				{
					parentName: 'sansa',
					name: 'content',
					title: 'Content',
					icon: 'assets/icons/navbar/lists.svg',
					iconAlias: 'nav_lists',
					isSelected: false,
					hasChildren: false,
					hoverOver: false,
					childOpen: false,
					route: '/content',
					childIndex: 0,
					click: () => {
						this.clickAnalyticsService.sendClickEvent(
							'click_content_library_page'
						);
					}
				}
			],
			// KEY 10, right after Content: insertion order is what places it.
			[
				10,
				{
					parentName: 'sansa',
					name: 'payments',
					title: 'Payments',
					icon: 'assets/icons/common/wallet.svg',
					iconAlias: 'wallet',
					isSelected: false,
					hasChildren: false,
					hoverOver: false,
					childOpen: false,
					route: '/payments',
					childIndex: 0
				}
			],
			[
				2,
				{
					parentName: 'sansa',
					name: 'ai-search',
					title: 'Discovery',
					icon: 'assets/icons/navbar/ai-agent.svg',
					iconAlias: 'nav_ai_agent',
					isSelected: false,
					hasChildren: false,
					hoverOver: false,
					childOpen: false,
					route: '/ai-search',
					childIndex: 0,
					click: () => {
						// Event id unchanged, for the reason above.
						this.clickAnalyticsService.sendClickEvent(
							'click_ai_agent_page'
						);
					}
				}
			],
			[
				3,
				{
					parentName: 'sansa',
					name: 'messaging',
					title: 'Messaging',
					icon: 'assets/icons/navbar/messaging.svg',
					iconAlias: 'nav_messaging',
					isSelected: false,
					hasChildren: false,
					hoverOver: false,
					childOpen: false,
					route: '/messaging',
					childIndex: 0,
					click: () => {
						this.clickAnalyticsService.sendClickEvent(
							'click_messaging_page'
						);
					}
				}
			],
			[
				4,
				{
					parentName: 'sansa',
					name: 'partner-ads',
					title: 'Partner Ads',
					icon: 'assets/icons/navbar/meta-mentions.svg',
					iconAlias: 'meta_mentions',
					isSelected: false,
					hasChildren: false,
					hoverOver: false,
					childOpen: false,
					route: '/partner-ads',
					childIndex: 0,
					hideOnMobile: true
				}
			],
			// [
			// 	5,
			// 	{
			// 		parentName: 'sansa',
			// 		name: 'social-listening',
			// 		title: 'Social Listening',
			// 		icon: 'assets/icons/navbar/social-listening.svg',
			// 		isSelected: false,
			// 		hasChildren: false,
			// 		hoverOver: false,
			// 		childOpen: false,
			// 		hideOnMobile: true,
			// 		route: '/social-listening',
			// 		childIndex: 0,
			// 		children: [
			// 			{
			// 				parentName: 'sansa',
			// 				name: 'general-setup',
			// 				isSelected: false,
			// 				hasChildren: false,
			// 				hoverOver: false,
			// 				childOpen: false,
			// 				childIndex: 0,
			// 				title: 'General Set Up',
			// 				icon: 'assets/icons/common/members.svg',
			// 				iconAlias: 'members',
			// 				route: '/general-setup'
			// 			},
			// 			{
			// 				parentName: 'sansa',
			// 				name: 'brand-setup',
			// 				isSelected: false,
			// 				hasChildren: false,
			// 				hoverOver: false,
			// 				childOpen: false,
			// 				childIndex: 0,
			// 				title: 'Brand Set Up',
			// 				icon: 'assets/icons/common/members.svg',
			// 				iconAlias: 'members',
			// 				route: '/brand-setup'
			// 			},
			// 			{
			// 				parentName: 'sansa',
			// 				name: 'competitors-setup',
			// 				isSelected: false,
			// 				hasChildren: false,
			// 				hoverOver: false,
			// 				childOpen: false,
			// 				childIndex: 0,
			// 				title: 'Competitors Set Up',
			// 				icon: 'assets/icons/common/members.svg',
			// 				iconAlias: 'members',
			// 				route: '/competitors-setup',
			// 				queryParams: 'projectId=1'
			// 			},
			// 			{
			// 				parentName: 'sansa',
			// 				name: 'industry-setup',
			// 				isSelected: false,
			// 				hasChildren: false,
			// 				hoverOver: false,
			// 				childOpen: false,
			// 				childIndex: 0,
			// 				title: 'Industry Set Up',
			// 				icon: 'assets/icons/common/members.svg',
			// 				iconAlias: 'members',
			// 				route: '/industry-setup'
			// 			}
			// 		]
			// 	}
			// ],
			[
				5,
				{
					parentName: 'sansa',
					name: 'social-listening',
					title: 'Social Listening',
					icon: 'assets/icons/navbar/social-listening.svg',
					iconAlias: 'nav_social_listening',
					isSelected: false,
					hasChildren: false,
					hoverOver: false,
					childOpen: false,
					hideOnMobile: true,
					route: '/social-listening',
					childIndex: 0,
					// Context-aware click + hover submenu — see onSl* handlers.
					slDynamic: true
				}
			],
			[
				7,
				{
					parentName: 'sansa',
					name: 'affiliates',
					title: 'Affiliates',
					icon: 'assets/icons/navbar/affiliates.svg',
					iconAlias: 'nav_affiliates',
					isSelected: false,
					hasChildren: false,
					hoverOver: false,
					childOpen: false,
					route: '/affiliates',
					childIndex: 0
				}
			],
			[
				8,
				{
					parentName: 'sansa',
					name: 'chrome-extension',
					title: 'Chrome Extension',
					icon: 'assets/icons/navbar/advanced-search.svg',
					iconAlias: 'nav_advanced_search',
					isSelected: false,
					hasChildren: false,
					hoverOver: false,
					childOpen: false,
					route: '/chrome-extension',
					childIndex: 0
				}
			],
			[
				9,
				{
					parentName: 'sansa',
					name: 'comment-autopilot',
					title: 'Autopilot',
					// Reuses the messaging glyph rather than adding an asset to
					// the shared library for a prototype section.
					icon: 'assets/icons/navbar/messaging.svg',
					// The rebuilt rail renders `iconAlias` through app-icon as a
					// CSS mask, so `icon` alone leaves the row with a blank
					// square — this entry was written before that rebuild and
					// merged in without it.
					iconAlias: 'nav_messaging',
					isSelected: false,
					hasChildren: false,
					hoverOver: false,
					childOpen: false,
					route: '/comment-autopilot',
					childIndex: 0
				}
			]
		]);

		this.router.events
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe((event: RouterEvent) => {
				if (event instanceof NavigationEnd) {
					this.urlOrigin = window.location.origin;
					this.sharedService.previousRoute = this.currentRoute;
					this.currentRoute = event?.urlAfterRedirects;

					this.hasSMI = event.url.split('/')[1] === 'influencer';
					this.activeRoute = event.url;
					// A guard may land somewhere other than the path the item
					// declared, so this reads urlAfterRedirects via router.url
					// rather than the requested one.
					this.recomputeActiveSubmenuItem();
				}
			});

		if (environment.Flags.hasTimestamp) {
			this.timestamp = buildGeneratedValues.timeStamp;
		}

		this.config.appendTo = 'body';
		this.config.bindValue = 'value';
	}

	public toggleHints(checked: boolean) {
		this.userStates.next({ ...this.userStates.value, show_hints: checked });
	}

	public ngOnInit(): void {
		this.restoreRailState();
		this.trackShellBackground();
		this.trackCoveringPanel();
		this.trackPanelVisibility();

		this.teamPermissionsState
			.ensureLoaded()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(() => {
				this.canManageBilling = this.teamPermissionsState.can(
					TeamAction.BILLING_MANAGE
				);
			});

		this.getAllData();

		this.prepareMenuList();

		// Autopilot is switched on per brand, so its entry waits for the answer:
		// absent first, drawn when a platform admin has enabled it. Only asked
		// on a build that carries the section at all, and re-reading the menu is
		// all it takes, because the visibility predicate reads the same state.
		if (isCommentAutopilotEnabled()) {
			this.commentAutopilotAccess
				.ensureLoaded()
				.pipe(takeUntil(this.unsubscriber$))
				.subscribe(() => {
					this.prepareMenuList();
					this.updateSidenavSelectedMenuItem(
						this.router.url.split('?')[0].split('/'),
						this.router.url
					);
					this.cdRef.markForCheck();
				});
		}

		// Highlight the page already on screen, before the first navigation
		// event this component can hear. The 500ms pass below stays because it
		// re-reads after the menu has finished filtering; without this one the
		// menu is blank for half a second on every hard reload, which only
		// became visible once the click stopped writing the highlight itself.
		this.updateSidenavSelectedMenuItem(
			this.router.url.split('?')[0].split('/'),
			this.router.url
		);

		this.hideSideBar = +localStorage.getItem('hideBar') || 0;

		this.updateBindValues();

		setTimeout(() => {
			const pathSegments = location.pathname.split('?')[0].split('/');
			this.updateSidenavSelectedMenuItem(
				pathSegments,
				location.pathname + location.search
			);
		}, 500);

		// THE HIGHLIGHT FOLLOWS THE URL THE APPLICATION SETTLED ON, which is the
		// only one that is true. It used to be recomputed on `NavigationStart`,
		// from the URL that was AIMED at, and `menuItemActive` additionally
		// marked the clicked item selected before navigating at all. Either of
		// those is wrong the moment a navigation does not arrive where it was
		// pointed: a guard's redirect, or a target Angular discards because it
		// equals the current URL, left the menu highlighting a department the
		// brand was not in — Campaigns pointed at itself while Home was on
		// screen. `urlAfterRedirects` is the settled one, and `NavigationCancel`
		// / `NavigationError` restore the highlight to the page the brand never
		// left.
		this.router.events
			.pipe(
				filter(
					event =>
						event instanceof NavigationEnd ||
						event instanceof NavigationCancel ||
						event instanceof NavigationError
				),
				takeUntil(this.unsubscriber$)
			)
			.subscribe((event: RouterEvent) => {
				const settledUrl =
					event instanceof NavigationEnd
						? event.urlAfterRedirects
						: this.router.url;
				this.updateSidenavSelectedMenuItem(
					settledUrl.split('?')[0].split('/'),
					settledUrl
				);
			});

		this.getFirstTimerData();
		this.sharedService.userProfileData$
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(res => {
				this.brandName = res?.display_brand_name ?? res?.company ?? '';
				this.profileImage =
					res?.display_brand_logo ?? res?.profile_image ?? '';
			});
		this.sharedService.triggerFirstTimeGetStarted.pipe(
			takeUntil(this.unsubscriber$)
		);

		this.sidenavService.setSidenav(this.sidenav);

		// The store is loaded here, and kept warm, because the Social Listening
		// click gate below reads it SYNCHRONOUSLY — a click must not wait on a
		// request. Each push redraws, so a purchase made on the plans page
		// reaches that gate without a reload.
		//
		// NO NAV ITEM IS LOCKED any more, and the absence is the decision.
		// `projects`, `messaging` and `negotiation` were disabled — greyed out,
		// and a click opened the "Discovery plan required" dialog — for a brand
		// with no `ProductAlias.DISCOVERY` subscription. The unified catalogue
		// retired that product and seeds `feature.discovery` on every tier, so
		// the lock applied to every customer and could be lifted by nobody.
		// Social Listening, Partner Ads and Affiliates were never locked here
		// and still are not: a click the tier cannot serve lands on that
		// department's own full-screen upsell page, which is an explanation
		// where a greyed-out item is only a closed door. Partner Ads no longer
		// even has that — the owner opened it on every tier on 2026-09-15, so
		// its flyout below opens for everybody.
		this.subscriptionsState
			.load()
			.pipe(
				takeUntil(this.unsubscriber$),
				catchError(() => of(null))
			)
			.subscribe(() => this.cdRef.markForCheck());

		this.subscriptionsState.subscriptions$
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(() => this.cdRef.markForCheck());
	}

	/**
	 * A row with children opens them IN THE LIST.
	 *
	 * The overlay this used to attach is gone from the rail — see
	 * `openSubmenuIndex`. `element` is kept in the signature because the
	 * template hands it in and a flyout is still what the collapsed rail would
	 * need if the accordion is ever refused.
	 */
	openChildrenMenu(_element: MatListItem, index: number) {
		const item = this.filteredMenuList[index];
		this.openSubmenu(index, this.childItems(item));
	}

	private openChildrenMenuOverlay(element: MatListItem, index: number) {
		if (this.overlayRef) {
			this.closeOverlay();
			return;
		}

		const item = this.filteredMenuList[index];

		const positionStrategy = this.overlay
			.position()
			.flexibleConnectedTo(element._hostElement)
			.withPositions([
				{
					originX: 'end',
					originY: 'top',
					overlayX: 'start',
					overlayY: 'top',
					offsetX: 4
				}
			])
			.withPush(false);

		this.overlayRef = this.overlay.create({
			positionStrategy,
			scrollStrategy: this.overlay.scrollStrategies.reposition()
		});

		const portal = new ComponentPortal(ChildrenMenuDialog);
		const componentRef = this.overlayRef.attach(portal);
		componentRef.instance.menuItems = item.children.map(child => {
			return {
				title: child.title,
				icon: child.iconAlias,
				path: `${item.route}${child.route}`
			} as ChildrenMenuItem;
		});
		componentRef.instance.clickItem
			.pipe(
				takeUntil(this.childrenMenuUnSubscriber),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(() => this.closeOverlay());

		fromEvent(document, 'click')
			.pipe(
				filter(event => {
					const clickTarget = event.target as Node;
					const clickedInsideOverlay =
						this.overlayRef?.overlayElement.contains(clickTarget);
					const clickedTrigger =
						element._hostElement.contains(clickTarget);
					return !clickedInsideOverlay && !clickedTrigger;
				}),
				takeUntil(this.childrenMenuUnSubscriber),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(() => {
				this.closeOverlay();
			});

		fromEvent<KeyboardEvent>(document, 'keydown')
			.pipe(
				filter(e => e.key === 'Escape'),
				takeUntil(this.childrenMenuUnSubscriber),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(() => this.closeOverlay());

		this.overlayRef
			.backdropClick()
			.pipe(
				takeUntil(this.childrenMenuUnSubscriber),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(() => {
				this.closeOverlay();
			});
	}

	closeOverlay() {
		this.overlayRef?.detach();
		this.overlayRef?.dispose();
		this.overlayRef = undefined;
		this.slMenuComponentRef = undefined;

		this.childrenMenuUnSubscriber.next();
	}

	// ── Social Listening sidebar item (slDynamic) ─────────────────────────

	/**
	 * Click on the Social Listening nav item: open the submenu. Always.
	 *
	 * It used to navigate instead — to General Setup when the account had no
	 * entity, to the first entity when the user was outside the section — and
	 * opened the flyout only when they were already inside it. General Setup
	 * is gone (each entity now has its own wizard), and with it the reason for
	 * a click to decide anything: the submenu IS the choice. Its six items are
	 * the same whatever the account holds, because each one's redirect guard
	 * resolves entity-vs-wizard on arrival — so there is nothing left for the
	 * click to resolve that the destination does not resolve better.
	 *
	 * Entity state is still loaded and still cached; it just no longer decides
	 * where a click goes.
	 */
	onSlClick(el: MatListItem): void {
		this.clearSlTimers();

		// A tier that does not carry Social Listening → go straight to the
		// full-screen upsell. Skips the entity lookups (they refuse without the
		// feature) and the submenu flyout; the route guard is the backstop for
		// deep links. Same rule as that guard, unreadable state included, so
		// the menu and the address bar agree.
		if (
			!this.subscriptionsState.canEnterProduct(
				SOCIAL_LISTENING_ACCESS.product
			)
		) {
			this.closeOverlay();
			this.router.navigateByUrl(SOCIAL_LISTENING_ACCESS.upsellPath);
			return;
		}

		// Open on whatever is known — cached state, or nothing on the very
		// first interaction — then refresh in the background. A click never
		// waits on the API, and the refresh REPLACES the items under an already
		// open row rather than reopening it, so the list does not flicker.
		const index = this.filteredMenuList.findIndex(i => i.slDynamic);
		const wasOpen = this.openSubmenuIndex === index;
		this.openSubmenu(index, this.slMenuItems(this.slNav.getCachedState()));
		if (wasOpen) {
			return;
		}
		this.slNav
			.loadEntities()
			.pipe(take(1), takeUntil(this.unsubscriber$))
			.subscribe(state => this.updateSlSubmenuItems(state));
	}

	/**
	 * No longer opens anything.
	 *
	 * Hover-to-open is a FLYOUT's idiom: the panel appears beside the cursor
	 * and vanishes when it leaves, so opening it by accident costs nothing. An
	 * accordion pushes every row below it down — doing that to a reader who was
	 * only travelling across the rail to reach Affiliates is the opposite of
	 * helpful. The handler stays wired so the row's hover state and the tier
	 * check keep their one place.
	 */
	onSlMouseEnter(el: MatListItem): void {
		return;
	}

	private onSlMouseEnterFlyout(el: MatListItem): void {
		// No Social Listening on this tier → no entity submenu (those APIs
		// refuse, and the click already routes to the upsell). Skip hover.
		if (
			!this.subscriptionsState.canEnterProduct(
				SOCIAL_LISTENING_ACCESS.product
			)
		) {
			return;
		}
		if (this.slCloseTimer) {
			clearTimeout(this.slCloseTimer);
			this.slCloseTimer = null;
		}
		if (this.overlayRef || this.slOpenTimer) {
			return;
		}
		this.slOpenTimer = setTimeout(() => {
			this.slOpenTimer = null;

			// The empty state used to suppress the flyout and let the click
			// fall through to General Setup. There is no General Setup to fall
			// through to, and an account with no entity is exactly the one
			// that needs to see the six choices — each item's redirect guard
			// takes it to that entity's wizard.
			//
			// Open instantly with whatever we know (cached or nothing), then
			// refresh asynchronously — hover never waits on the API.
			this.openSlSubmenu(el, this.slNav.getCachedState());
			this.slNav
				.loadEntities()
				.pipe(take(1), takeUntil(this.unsubscriber$))
				.subscribe(state => this.updateSlSubmenuItems(state));
		}, this.SL_HOVER_OPEN_DELAY);
	}

	onSlMouseLeave(): void {
		if (this.slOpenTimer) {
			clearTimeout(this.slOpenTimer);
			this.slOpenTimer = null;
		}
		this.scheduleSlClose();
	}

	private scheduleSlClose(): void {
		if (this.slCloseTimer) {
			clearTimeout(this.slCloseTimer);
		}
		this.slCloseTimer = setTimeout(() => {
			this.slCloseTimer = null;
			this.closeOverlay();
		}, this.SL_HOVER_CLOSE_DELAY);
	}

	private clearSlTimers(): void {
		if (this.slOpenTimer) {
			clearTimeout(this.slOpenTimer);
			this.slOpenTimer = null;
		}
		if (this.slCloseTimer) {
			clearTimeout(this.slCloseTimer);
			this.slCloseTimer = null;
		}
	}

	private slMenuItems(state: SlEntitiesState | null): ChildrenMenuItem[] {
		return this.slNav.buildSubmenu(state).map(i => ({
			title: i.title,
			icon: i.icon,
			path: i.path,
			disabled: i.disabled,
			tooltip: i.tooltip,
			matches: i.matches
		}));
	}

	/**
	 * Replace what the open row is showing, in place.
	 *
	 * Guarded on the row still being open: the entity load is asynchronous and
	 * the reader may have shut the list, or opened another one, before it
	 * lands. Writing the items back then would reopen a list nobody asked for.
	 */
	private updateSlSubmenuItems(state: SlEntitiesState | null): void {
		const index = this.filteredMenuList.findIndex(i => i.slDynamic);
		if (this.openSubmenuIndex !== index) {
			return;
		}
		this.openSubmenuItems = this.slMenuItems(state);
		this.recomputeActiveSubmenuItem();
	}

	/**
	 * Open the SL submenu flyout. Mirrors `openChildrenMenu`'s overlay wiring
	 * but with dynamic items and hover keep-open / leave-close on the overlay
	 * itself (so the cursor can travel from the nav item to the flyout).
	 */
	private openSlSubmenu(
		el: MatListItem,
		state: SlEntitiesState | null
	): void {
		if (this.overlayRef) {
			this.updateSlSubmenuItems(state);
			return;
		}

		const positionStrategy = this.overlay
			.position()
			.flexibleConnectedTo(el._hostElement)
			.withPositions([
				{
					originX: 'end',
					originY: 'top',
					overlayX: 'start',
					overlayY: 'top',
					offsetX: 4
				}
			])
			.withPush(false);

		this.overlayRef = this.overlay.create({
			positionStrategy,
			scrollStrategy: this.overlay.scrollStrategies.reposition()
		});

		const portal = new ComponentPortal(ChildrenMenuDialog);
		this.slMenuComponentRef = this.overlayRef.attach(portal);
		this.slMenuComponentRef.instance.menuItems = this.slMenuItems(state);
		this.slMenuComponentRef.instance.clickItem
			.pipe(
				takeUntil(this.childrenMenuUnSubscriber),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(() => this.closeOverlay());

		// Keep open while hovering the flyout; close shortly after leaving it.
		const overlayEl = this.overlayRef.overlayElement;
		fromEvent(overlayEl, 'mouseenter')
			.pipe(
				takeUntil(this.childrenMenuUnSubscriber),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(() => {
				if (this.slCloseTimer) {
					clearTimeout(this.slCloseTimer);
					this.slCloseTimer = null;
				}
			});
		fromEvent(overlayEl, 'mouseleave')
			.pipe(
				takeUntil(this.childrenMenuUnSubscriber),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(() => this.scheduleSlClose());

		fromEvent(document, 'click')
			.pipe(
				filter(event => {
					const target = event.target as Node;
					const insideOverlay =
						this.overlayRef?.overlayElement.contains(target);
					const onTrigger = el._hostElement.contains(target);
					return !insideOverlay && !onTrigger;
				}),
				takeUntil(this.childrenMenuUnSubscriber),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(() => this.closeOverlay());

		fromEvent<KeyboardEvent>(document, 'keydown')
			.pipe(
				filter(e => e.key === 'Escape'),
				takeUntil(this.childrenMenuUnSubscriber),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(() => this.closeOverlay());
	}

	prepareMenuList() {
		this.filteredMenuList = [];
		for (const item of this.productMenuGroup.values()) {
			// EVERY ENTRY MUST BE STATED IN THE ACCESS TABLE, and this is the
			// line that enforces it. Asked before the visibility filters below,
			// so an entry hidden on this build is checked too.
			//
			// It throws, and the throw is the point: an entry whose product and
			// treatment are stated nowhere is exactly what Campaigns was. The
			// padlock map was keyed `projects`, the item was renamed to
			// `campaigns`, nothing matched, and the result was not an error but
			// a THIRD behaviour nobody designed — a normal-looking item, an
			// error toast from the interceptor and a bounce to Home. A rename
			// that forgets the table now fails here instead, naming the entry.
			const access = requireNavigationAccess(item.name);

			// The build's own flags: the same predicate the universal search and,
			// for Comment Autopilot, the router read.
			if (!isNavigationEntryShown(access.entry)) {
				continue;
			}
			this.filteredMenuList.push(item);
		}
	}

	clickContact() {
		this.clickAnalyticsService.sendClickEvent('click_contact_sales');
		this.bookingService.openFullscreenBanner(false);
	}

	private updateSidenavSelectedMenuItem(
		pathSegments: string[],
		fullUrl = ''
	) {
		let firstPathSegment =
			pathSegments.length > 1 && pathSegments[1] ? pathSegments[1] : '';
		firstPathSegment = '/' + firstPathSegment;
		// CONTRACT: Campaigns opens the programme wizard under /affiliates with from=campaigns, so that one entry belongs to Campaigns in the rail; every other /affiliates URL stays Affiliates.
		if (
			firstPathSegment === '/affiliates' &&
			this.router.parseUrl(fullUrl).queryParams['from'] === 'campaigns'
		) {
			firstPathSegment = '/campaigns';
		}
		this.filteredMenuList.forEach(v => {
			if (firstPathSegment === v.route) v.isSelected = true;
			else v.isSelected = false;
		});

		if (firstPathSegment === '/discovery') {
			this.userStatesService.hintsDisabled = false;
		} else {
			this.userStatesService.hintsDisabled = true;
		}
	}

	public getFirstTimerData(): void {
		this.getStartedLoading = true;
		this.sharedService
			.getStartedData()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				res => {
					this.sharedService.firstTimerData$.next(
						res?.first_time_user
					);
					this.getStartedData = res?.get_started_data;
					this.sharedService.getStartedData$.next(
						res?.get_started_data
					);
					this.getStartedLoading = false;
				},
				() => {
					this.getStartedLoading = false;
				}
			);
	}

	public getAllData(): void {
		this.allDataLoading = true;

		this.landingPageService
			.getAllData()
			.pipe(
				takeUntil(this.unsubscriber$),
				finalize(() => (this.allDataLoading = false))
			)
			.subscribe(
				res => {
					this.sharedService.userProfileData$.next(res?.data);
				},
				err => {}
			);
	}

	public ngAfterViewChecked(): void {
		this.updateBindValues();
		this.cdRef.detectChanges();
	}

	private updateBindValues(): void {
		const storageJwt = JSON.parse(
			localStorage.getItem(StorageKey.decodedJwtIo)
		);

		this.userFName = storageJwt?.['user_firstname'];
		this.userLName = storageJwt?.['user_lastname'];
		this.userEmail = storageJwt?.['sub'];
	}

	toggleProfileAction(): void {
		this.profileOpen
			? (this.profileOpen = false)
			: (this.profileOpen = true);
	}

	toggleBurgerMenu() {
		this.burgerMenuOpen = !this.burgerMenuOpen;
	}

	public redirectToHome(): void {
		this.router.navigate(['']);
	}

	menuItemTrigger(index: number): void {
		const menuItemObj = this.filteredMenuList[index];
		if (menuItemObj?.name === 'onboarding') {
			if (menuItemObj.click) {
				menuItemObj.click();
			}

			return;
		}
		this.menuItemActive(index);
	}

	public bookADemoLink(): void {
		window.open(UtilsService.BOOK_DEMO_URL, '_blank');
	}

	/**
	 * `isSelected` IS NOT WRITTEN HERE. A click states where the brand is going,
	 * not where it is; the highlight is recomputed from the URL the router
	 * settles on (see the subscription in `ngOnInit`), so a navigation that is
	 * redirected, refused or discarded cannot leave the menu pointing at a page
	 * the brand never reached. Everything else this method does — the expanded
	 * state, the flyout, the per-item analytics click — is about the click and
	 * stays.
	 */
	menuItemActive(index: number, pushRoute = true): void {
		for (let i = 0; i < this.filteredMenuList.length; i++) {
			if (i === index) {
				if (this.filteredMenuList[i].hasChildren) {
					this.expandMenu = true;
					this.authenticationService.expandMenu$.next(
						this.expandMenu
					);
				} else {
					this.expandMenu = false;
					this.authenticationService.expandMenu$.next(
						this.expandMenu
					);
				}
				break;
			}
		}
		this.activeParentIndex = index;
		if (this.filteredMenuList[index].name === 'audience-creator') {
			this.authenticationService.triggerNavRoute$.next('audience');
		}
		if (this.filteredMenuList[index].name === 'copywriter') {
			this.authenticationService.triggerNavRoute$.next('content');
		}
		if (this.filteredMenuList[index].name === 'campaign-builder') {
			this.authenticationService.triggerNavRoute$.next('google');
		}
		if (!this.filteredMenuList[index].hasChildren) {
			if (this.filteredMenuList[index].click) {
				this.filteredMenuList[index].click();
			}

			if (this.filteredMenuList[index].queryParams)
				this.router.navigate([this.filteredMenuList[index].route], {
					queryParams: this.filteredMenuList[index].queryParams
				});
			else this.router.navigate([this.filteredMenuList[index].route]);
		}
		if (!this.filteredMenuList[index].hasChildren) {
			this.filteredMenuList.forEach(item => {
				item.childOpen = false;
			});
		} else {
			this.expandMenu = true;
			this.authenticationService.expandMenu$.next(this.expandMenu);
			this.filteredMenuList[index].childOpen = true;
		}
	}

	public mouseEnterMenu(index: number): void {
		this.filteredMenuList.forEach(item => {
			item.hoverOver = false;
		});
		for (let i = 0; i < this.filteredMenuList.length; i++) {
			if (i === index) {
				this.filteredMenuList[i].hoverOver = true;
				break;
			}
		}
	}

	public mouseLeaveMenu(index): void {
		this.filteredMenuList.forEach(item => {
			item.hoverOver = false;
		});
	}

	public mouseEnterMenuEffect(): Object {
		const styles = {
			background: '#DADCE0'
		};
		return styles;
	}

	public menuItemSelectedEffect(): Object {
		const styles = {
			'border-left': `5px solid ${this.primaryColor}`
		};
		return styles;
	}

	public ngAfterViewInit(): void {
		this.publishScrollerGutter();
	}

	public ngOnDestroy(): void {
		// The shell is torn down on sign-out, and the page it leaves behind has
		// no rail for this class to describe.
		document.body.classList.remove(RAIL_COLLAPSED_BODY_CLASS);
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
		this.removeSubscriptions();
		if (
			this.notificationSubscription &&
			!this.notificationSubscription.closed
		) {
			this.notificationSubscription.unsubscribe();
		}
		clearInterval(this.resetTimerInterval);
		this.clearPanelDetachTimer();
		this.scrollerGutterObserver?.disconnect();
		this.clearSlTimers();
		this.cancelPageSurfaceChecks();
	}

	public onScroll(event: any): void {
		this.scrollService.changeScrollY(event.target.scrollTop);
	}

	/**
	 * The page declares the background the shell paints behind the Ask Ana
	 * panel, as `shellBackground` in its route data — it does not reach into
	 * the shell's DOM to set it, which is what the Home page used to do and
	 * why every other page showed an unrelated white at the panel's rounded
	 * corner.
	 *
	 * Between two routes the shell clears the declaration rather than keeping
	 * the one the page being left made: the transition backdrop is the shell's
	 * own default. The route-change spinner keeps its own stated scrim and
	 * sits on top of that.
	 *
	 * A navigation that never arrives is re-read, not left cleared: a guard
	 * that returns false (NavigationCancel) or a failed lazy chunk
	 * (NavigationError) leaves the brand on the page that declared the
	 * background, so the declaration is still the right one.
	 */
	private trackShellBackground(): void {
		this.declaredShellBackground = this.readRouteShellBackground();
		this.publishShellBackground();
		this.schedulePageSurfaceChecks();
		this.router.events
			.pipe(
				filter(
					event =>
						event instanceof NavigationStart ||
						event instanceof NavigationEnd ||
						event instanceof NavigationCancel ||
						event instanceof NavigationError
				),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(event => {
				if (event instanceof NavigationStart) {
					// TRAP: the detected colour is NOT cleared here — the page being left
					// stays on screen through the lazy chunk and the resolvers, and its room
					// would show white the whole time. The next read replaces it.
					this.cancelPageSurfaceChecks();
					this.declaredShellBackground = null;
				} else {
					this.declaredShellBackground =
						this.readRouteShellBackground();
					this.schedulePageSurfaceChecks();
				}
				this.publishShellBackground();
			});
	}

	private publishShellBackground(): void {
		this.shellBackground =
			this.declaredShellBackground ?? this.detectedPageBackground;
	}

	/**
	 * A page that paints its own background on a host or wrapper ends that
	 * surface where its content ends, so without this the room reserved under
	 * the composer showed the scroller's white beneath a tinted page.
	 *
	 * Read on the next frame — the routed view is rendered by then — and again
	 * later for pages whose surface arrives with their data.
	 */
	private schedulePageSurfaceChecks(): void {
		this.cancelPageSurfaceChecks();
		if (this.declaredShellBackground !== null) {
			this.detectedPageBackground = null;
			return;
		}
		this.ngZone.runOutsideAngular(() => {
			this.pageSurfaceFrame = requestAnimationFrame(() => {
				this.pageSurfaceFrame = null;
				this.refreshPageBackground();
			});
			this.pageSurfaceTimers =
				SidenavGroupComponent.PAGE_SURFACE_RECHECK_MS.map(delay =>
					setTimeout(() => this.refreshPageBackground(), delay)
				);
		});
	}

	private cancelPageSurfaceChecks(): void {
		if (this.pageSurfaceFrame !== null) {
			cancelAnimationFrame(this.pageSurfaceFrame);
			this.pageSurfaceFrame = null;
		}
		this.pageSurfaceTimers.forEach(timer => clearTimeout(timer));
		this.pageSurfaceTimers = [];
	}

	/** Runs outside Angular; re-enters only when the colour actually changed. */
	private refreshPageBackground(): void {
		const detected = this.detectPageSurfaceColor();
		if (detected === this.detectedPageBackground) {
			return;
		}
		this.ngZone.run(() => {
			this.detectedPageBackground = detected;
			this.publishShellBackground();
		});
	}

	/**
	 * The page's bottom surface: of the opaque descendants of the content column
	 * that span its full width and at least half its content height, the one
	 * whose bottom edge is lowest. Ties go to the later element in document
	 * order, the one painted on top.
	 */
	// TRAP: searched BOTTOM-UP — a white bar at the top of a tinted page (the
	// messaging overview's switcher) is full-width too. And a surface must cover
	// half the column: a full-width tinted hero above a transparent page would
	// otherwise recolour the whole page behind it.
	private detectPageSurfaceColor(): string | null {
		const column = this.shellContentMain?.nativeElement;
		if (!column) {
			return null;
		}
		const columnBox = column.getBoundingClientRect();
		if (columnBox.width === 0) {
			return null;
		}
		const minimumHeight =
			(columnBox.height -
				parseFloat(getComputedStyle(column).paddingBottom || '0')) /
			2;
		let lowestBottom = -Infinity;
		let color: string | null = null;
		const visit = (element: Element, depth: number): void => {
			for (const child of Array.from(element.children)) {
				if (child.tagName === 'APP-LOLLY-SPINNING-LOADER') {
					continue;
				}
				const box = child.getBoundingClientRect();
				if (
					box.height >= minimumHeight &&
					box.bottom >= lowestBottom &&
					Math.abs(box.left - columnBox.left) <= 2 &&
					Math.abs(box.right - columnBox.right) <= 2
				) {
					const background = getComputedStyle(child).backgroundColor;
					if (isOpaqueColor(background)) {
						lowestBottom = box.bottom;
						color = background;
					}
				}
				if (depth < SidenavGroupComponent.PAGE_SURFACE_MAX_DEPTH) {
					visit(child, depth + 1);
				}
			}
		};
		visit(column, 1);
		return color;
	}

	/** Deepest declaration wins, so a child route can restate its parent's. */
	private readRouteShellBackground(): string | null {
		let route: ActivatedRoute | null = this.router.routerState.root;
		let declared: string | null = null;
		while (route) {
			const value = route.snapshot.data?.['shellBackground'];
			if (typeof value === 'string') {
				declared = value;
			}
			route = route.firstChild;
		}
		return declared;
	}

	/**
	 * What the shell owes the page while the panel covers it: the page is not
	 * re-laid-out, and it does not scroll behind the panel.
	 *
	 * The panel leaves the content flow so the page keeps the layout it had,
	 * and stays out of it until the width animation has finished — re-entering
	 * the flow at the covering width would squeeze the page through every
	 * frame on the way out. The scroller's overflow is hidden meanwhile, which
	 * discards scrollTop, so it is captured going in and put back on release.
	 */
	private trackCoveringPanel(): void {
		this.anaFacade.coversContent$
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(covering => this.applyCoveringPanel(covering));
	}

	private applyCoveringPanel(covering: boolean): void {
		// The timer is only ever cleared on a branch that re-arms or replaces
		// it. Clearing it before the early return below would let a repeated
		// `false` cancel the pending release and leave the panel detached for
		// the rest of the session.
		if (covering === this.shellScrollLocked) {
			return;
		}

		const scroller = this.shellScroller?.nativeElement;

		if (covering) {
			this.clearPanelDetachTimer();
			this.lockedScrollTop = scroller ? scroller.scrollTop : 0;
			this.panelDetached = true;
			this.shellScrollLocked = true;
			this.cdRef.detectChanges();
			return;
		}

		this.shellScrollLocked = false;
		this.cdRef.detectChanges();
		if (scroller) {
			scroller.scrollTop = this.lockedScrollTop;
		}
		this.armPanelDetachRelease();
	}

	// TRAP: a plain open or close takes the panel out of flow as well -- left in
	// flow, its width transition re-lays-out the page on every frame. skip(1)
	// drops the replayed current value, which is not an animation.
	private trackPanelVisibility(): void {
		this.anaFacade.visible$
			.pipe(
				distinctUntilChanged(),
				skip(1),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(() => {
				this.panelDetached = true;
				this.cdRef.detectChanges();
				this.armPanelDetachRelease();
			});
	}

	private armPanelDetachRelease(): void {
		this.clearPanelDetachTimer();
		this.panelDetachTimer = setTimeout(() => {
			this.panelDetachTimer = null;
			// TRAP: a covering hold outlives this timer; applyCoveringPanel
			// alone releases it, and re-arms this timer when it does.
			if (this.shellScrollLocked) {
				return;
			}
			this.panelDetached = false;
			this.cdRef.detectChanges();
		}, SidenavGroupComponent.PANEL_DETACH_RELEASE_MS);
	}

	// CONTRACT: the out-of-flow panel is pinned to the viewport, the in-flow one
	// to the scroller's content edge -- they differ by the stable scrollbar
	// gutter, which the stylesheet reads back as the covering panel's right.
	private publishScrollerGutter(): void {
		const scroller = this.shellScroller?.nativeElement;
		if (!scroller) {
			return;
		}
		this.ngZone.runOutsideAngular(() => {
			this.scrollerGutterObserver = new ResizeObserver(() => {
				scroller.style.setProperty(
					'--shell-scroller-gutter',
					`${scroller.offsetWidth - scroller.clientWidth}px`
				);
			});
			this.scrollerGutterObserver.observe(scroller);
		});
	}

	private clearPanelDetachTimer(): void {
		if (this.panelDetachTimer !== null) {
			clearTimeout(this.panelDetachTimer);
			this.panelDetachTimer = null;
		}
	}

	public openKnowledgeBase() {
		return this.dialog.open(KnowledgeBaseComponent, {
			width: '600px',
			height: '100%',
			position: {
				right: '0'
			},
			panelClass: [
				'knowledge-base-dialog',
				'matdialog-no-padding',
				'slide',
				'slideInRight'
			]
		});
	}

	public createTicket() {
		this.profileOpen = false;
		this.burgerMenuOpen = false;

		const dialog = this.openKnowledgeBase();

		dialog.componentInstance.openTicketCreation();
	}

	public openYourTickets() {
		this.profileOpen = false;
		this.burgerMenuOpen = false;

		const dialog = this.dialog.open(KnowledgeBaseComponent, {
			width: '600px',
			height: '100%',
			position: {
				right: '0'
			},
			data: { hideBack: true },
			panelClass: [
				'knowledge-base-dialog',
				'matdialog-no-padding',
				'slide',
				'slideInRight'
			]
		});

		dialog.componentInstance.openViewTicket();
	}

	public logout(): void {
		this.profileOpen = false;
		this.burgerMenuOpen = false;

		this.authenticationService.expandMenu$.next(false);
		this.authV2Service.signoutUser();
	}

	navigate(
		target:
			| 'ai_agent'
			| 'projects'
			| 'partner-ads'
			| 'social-listening'
			| 'messaging'
	) {
		this.profileOpen = false;
		this.burgerMenuOpen = false;

		switch (target) {
			case 'ai_agent':
				this.router.navigate(['/ai-search']);
				break;
			case 'projects':
				this.router.navigate(['/campaigns']);
				break;
			case 'partner-ads':
				this.router.navigate(['/partner-ads']);
				break;
			case 'social-listening':
				this.router.navigate(['/social-listening']);
				break;
			case 'messaging':
				this.router.navigate(['/messaging']);
				break;
		}
	}

	private removeSubscriptions(): void {
		this.expandAccountMenuSubscription.unsubscribe();
	}

	public goToSettings(): void {
		this.profileOpen = false;
		this.burgerMenuOpen = false;

		this.router.navigate(['/user-management/my-profile']);
	}

	public goToUpgrade() {
		this.profileOpen = false;
		this.burgerMenuOpen = false;

		this.router.navigate(['user-management/manage-plan'], {
			queryParams: { openPlans: true }
		});
	}
}
